import api from './api';

/**
 * Lots attribués (lot 057) : un transporteur partenaire vend à la société un lot
 * de camions chargés, à un prix au litre unique. Chemins et formes de réponse :
 * `backend/app/api/v1/lots.py` et `backend/app/services/lot_service.py`.
 *
 * Aucun calcul ici : l'écran affiche ce que le serveur renvoie. Les montants
 * (prix au litre, valeurs du lot) ne sont présents que pour qui voit l'argent ;
 * un champ absent n'est jamais lu comme zéro.
 */

const BASE = '/lots';
// Les routes de collection du serveur sont déclarées avec la barre finale
// (`/lots/`) : on l'écrit, plutôt que de compter sur une redirection qui
// rejouerait mal un POST.
const COLLECTION = `${BASE}/`;

/** URL de l'impression du suivi (mêmes filtres que `GET /lots/trucks`). */
export const IMPRESSION_SUIVI_LOTS = '/print/lot-trucks';

/**
 * Corps d'une réponse, avec ou sans enveloppe `{ success, data }`. Une donnée
 * `null` dans l'enveloppe reste `null` : elle n'est pas remplacée par
 * l'enveloppe elle-même.
 */
export const deballer = (reponse) => {
  const corps = reponse?.data;
  if (
    corps &&
    typeof corps === 'object' &&
    !Array.isArray(corps) &&
    'success' in corps &&
    'data' in corps
  ) {
    return corps.data;
  }
  return corps;
};

/** Clé d'idempotence d'une écriture : générée une fois, rejouée tant que l'envoi n'a pas abouti. */
export const nouvelleCleLot = (prefixe = 'lots') =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${prefixe}-${Date.now()}-${Math.random().toString(16).slice(2)}`;

/** Paramètres de requête sans valeur vide (un filtre vide ne part pas). */
const sansVides = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, valeur]) => valeur !== undefined && valeur !== null && valeur !== '',
    ),
  );

const entierEnTete = (reponse, nom) => {
  const brut = reponse?.headers?.[nom];
  if (brut === undefined || brut === null) return null;
  const nombre = Number.parseInt(brut, 10);
  return Number.isNaN(nombre) ? null : nombre;
};

export const lotsAPI = {
  // --- Lecture ------------------------------------------------------------------
  async list(params = {}) {
    const lots = deballer(await api.get(COLLECTION, { params: sansVides(params) }));
    return Array.isArray(lots) ? lots : [];
  },
  async dashboard(params = {}) {
    return deballer(await api.get(`${BASE}/dashboard`, { params: sansVides(params) }));
  },
  /** Les camions de tous les lots : `{ items, total }` (en-tête `X-Total-Count`). */
  async trucks(params = {}) {
    const reponse = await api.get(`${BASE}/trucks`, { params: sansVides(params) });
    const items = deballer(reponse);
    const lignes = Array.isArray(items) ? items : [];
    return { items: lignes, total: entierEnTete(reponse, 'x-total-count') ?? lignes.length };
  },
  /** En-tête, compteurs et camions d'un lot : `{ lot, progress, lines, money? }`. */
  async get(lotId, options = {}) {
    return deballer(await api.get(`${BASE}/${lotId}`, options));
  },

  // --- Écriture : chaque appel porte sa clé d'idempotence ---------------------------
  async create(corps, idempotencyKey) {
    return deballer(
      await api.post(COLLECTION, { ...corps, idempotency_key: idempotencyKey || nouvelleCleLot() }),
    );
  },
  async update(lotId, corps, idempotencyKey) {
    return deballer(
      await api.patch(`${BASE}/${lotId}`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot(),
      }),
    );
  },
  async addTrucks(lotId, trucks, idempotencyKey) {
    return deballer(
      await api.post(`${BASE}/${lotId}/trucks`, {
        trucks,
        idempotency_key: idempotencyKey || nouvelleCleLot(),
      }),
    );
  },
  async updateTruck(lotId, lineId, corps, idempotencyKey) {
    return deballer(
      await api.patch(`${BASE}/${lotId}/trucks/${lineId}`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot(),
      }),
    );
  },
  async removeTruck(lotId, lineId, idempotencyKey) {
    return deballer(
      await api.delete(`${BASE}/${lotId}/trucks/${lineId}`, {
        headers: { 'Idempotency-Key': idempotencyKey || nouvelleCleLot() },
      }),
    );
  },
  /**
   * Confirmation groupée. Rend `{ created, rejected, unchanged, progress }` :
   * un camion refusé n'empêche pas les autres.
   */
  async confirm(lotId, corps, idempotencyKey) {
    return deballer(
      await api.post(`${BASE}/${lotId}/confirm`, {
        ...corps,
        idempotency_key: idempotencyKey || nouvelleCleLot(),
      }),
    );
  },
  /** « Départ retour » : `{ changed, unchanged, refused }`. */
  async startReturns({ lineIds, at, note, idempotencyKey }) {
    return deballer(
      await api.post(`${BASE}/returns/start`, {
        line_ids: lineIds,
        at: at || undefined,
        note: note || undefined,
        idempotency_key: idempotencyKey || nouvelleCleLot(),
      }),
    );
  },
  /** « Rendu » : `{ changed, unchanged, refused }`. */
  async finishReturns({ lineIds, at, note, idempotencyKey }) {
    return deballer(
      await api.post(`${BASE}/returns/done`, {
        line_ids: lineIds,
        at: at || undefined,
        note: note || undefined,
        idempotency_key: idempotencyKey || nouvelleCleLot(),
      }),
    );
  },

  /**
   * Envoie au responsable (administrateurs de la société, sur WhatsApp) le PDF
   * du suivi avec exactement ces filtres.
   */
  async sendTrucksList(params, idempotencyKey) {
    const reponse = await api.post(`${IMPRESSION_SUIVI_LOTS}/send`, {
      ...sansVides(params),
      idempotency_key: idempotencyKey || nouvelleCleLot(),
    });
    return reponse.data;
  },
};

export default lotsAPI;
